/**
 * Страница YouTube
 * Автор: Cursor Generator
 * Описание: Загрузка видео, транскрибация, генерация описания и публикация на YouTube
 */

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import MagneticButton from '../components/MagneticButton';
import AnimatedCard from '../components/AnimatedCard';
import ProgressBar from '../components/ProgressBar';
import SkeletonLoader from '../components/SkeletonLoader';
import { showToast, initToast } from '../components/Toast';
import { API_URL } from '../utils/api.js';
import { isNative, pickVideoFromGallery, shareContent, hapticFeedback, canRunLocalTranscription, getDeviceInfo } from '../utils/native.js';
import { getTranscriptionInfo, transcribeLocal, loadWebWhisperModel, isModelLoaded } from '../utils/localWhisper.js';
import UploadProgress, { UploadProgressItem } from '../components/UploadProgress';
import { validateFiles, generateFileId } from '../utils/uploadValidation.js';
import { uploadFileInChunks, cancelUpload, checkServerUploadStatus } from '../utils/chunkedUpload.js';
import { uploadWithTus, cancelTusUpload, hasPendingTusUpload } from '../utils/tusUpload.js';
import { authHelpers } from '../lib/supabase.js';

// Метод загрузки: 'tus' для надёжной загрузки, 'chunked' для совместимости
// TUS работает на отдельном порту 4001 через Nginx proxy
const UPLOAD_METHOD = 'tus';

const MODEL_OPTIONS = {
  openrouter: [
    { 
      value: 'google/gemini-2.5-flash', 
      label: 'Gemini 2.5 Flash',
      description: '💰 $0.027/$0.225 за 1M токенов | ⭐⭐⭐⭐ Качество | ⚡⚡⚡⚡ Очень быстро | ✅ РЕКОМЕНДУЕТСЯ - самая дешевая'
    },
    { 
      value: 'google/gemini-1.5-flash', 
      label: 'Gemini 1.5 Flash',
      description: '💰 $0.075/$0.30 за 1M токенов | ⭐⭐⭐⭐ Качество | ⚡⚡⚡⚡ Очень быстро | ✅ Дешевая альтернатива'
    },
    { 
      value: 'openai/gpt-4o-mini', 
      label: 'GPT-4o Mini',
      description: '💰 $0.15/$0.60 за 1M токенов | ⭐⭐⭐⭐ Качество | ⚡⚡⚡⚡ Очень быстро | ✅ Отличный баланс цена/качество'
    },
    { 
      value: 'deepseek/deepseek-chat', 
      label: 'DeepSeek Chat',
      description: '💰 $0.14/$0.28 за 1M токенов | ⭐⭐⭐⭐ Качество | ⚡⚡⚡ Быстро | ✅ Дешево и качественно'
    },
    { 
      value: 'openai/gpt-3.5-turbo', 
      label: 'GPT-3.5 Turbo',
      description: '💰 $0.50/$1.50 за 1M токенов | ⭐⭐⭐⭐ Качество | ⚡⚡⚡ Быстро | ✅ Дешевая и надежная'
    },
    { 
      value: 'meta-llama/llama-3.1-70b-instruct', 
      label: 'Llama 3.1 70B',
      description: '💰 $0.59/$0.79 за 1M токенов | ⭐⭐⭐⭐ Качество | ⚡⚡ Средне | ✅ Хороший баланс'
    },
    { 
      value: 'openai/gpt-4o', 
      label: 'GPT-4o',
      description: '💰 $2.50/$10.00 за 1M токенов | ⭐⭐⭐⭐⭐ Качество | ⚡⚡⚡ Быстро | ⚠️ Дорого, но максимальное качество'
    },
  ],
  openai: [
    { 
      value: 'gpt-4o-mini', 
      label: 'GPT-4o Mini',
      description: '💰 $0.15/$0.60 за 1M токенов | ⭐⭐⭐⭐ Качество | ⚡⚡⚡⚡ Очень быстро | ✅ Дешево и качественно'
    },
    { 
      value: 'gpt-4o', 
      label: 'GPT-4o',
      description: '💰 $2.50/$10.00 за 1M токенов | ⭐⭐⭐⭐⭐ Качество | ⚡⚡⚡ Быстро | ⚠️ Дорого, но максимальное качество'
    },
  ],
};

// Библиотеки для транскрибации с рекомендациями по серверу
const TRANSCRIPTION_LIBRARIES = {
  // Локальные библиотеки (работают на вашем сервере)
  local: {
    name: 'Faster-Whisper (локально)',
    description: 'Быстрая локальная транскрибация без API (установлена)',
    models: [
      { 
        value: 'tiny', 
        label: 'Tiny - Самая легкая',
        quality: '⭐ Базовое качество',
        speed: '⚡⚡⚡ Очень быстро',
        timePerMinute: '~10-30 сек/мин',
        recommended: 'Для быстрой транскрибации'
      },
      { 
        value: 'base', 
        label: 'Base - Баланс',
        quality: '⭐⭐ Хорошее качество',
        speed: '⚡⚡ Быстро',
        timePerMinute: '~30-60 сек/мин',
        recommended: 'Рекомендуется для большинства случаев'
      },
      { 
        value: 'small', 
        label: 'Small - Лучше качество',
        quality: '⭐⭐⭐ Отличное качество',
        speed: '⚡ Средне',
        timePerMinute: '~1-2 мин/мин',
        recommended: 'Для качественной транскрибации'
      },
      { 
        value: 'medium', 
        label: 'Medium - Высокое качество',
        quality: '⭐⭐⭐⭐ Высокое качество',
        speed: '🐢 Медленно',
        timePerMinute: '~2-4 мин/мин',
        recommended: 'Для высокого качества (медленно)',
        disabled: true,
        disabledReason: '🔜 Скоро будет доступно'
      },
      { 
        value: 'large-v2', 
        label: 'Large V2 - Лучшее качество',
        quality: '⭐⭐⭐⭐⭐ Максимальное качество',
        speed: '🐢🐢 Очень медленно',
        timePerMinute: '~4-8 мин/мин',
        recommended: 'Максимальное качество (очень медленно)',
        disabled: true,
        disabledReason: '🔜 Скоро будет доступно'
      },
      { 
        value: 'large-v3', 
        label: 'Large V3 - Максимальное качество',
        quality: '⭐⭐⭐⭐⭐ Максимальное качество',
        speed: '🐢🐢🐢 Очень медленно',
        timePerMinute: '~4-8 мин/мин',
        recommended: 'Максимальное качество (очень медленно)',
        disabled: true,
        disabledReason: '🔜 Скоро будет доступно'
      },
    ],
    requirements: {
      min_ram: '1 GB',
      recommended_ram: '4 GB',
      min_cpu: '1 ядро',
      recommended_cpu: '4 ядра',
      gpu: 'Опционально (ускоряет в 5-10 раз)',
      disk: '2-5 GB для моделей',
      install: 'pip install faster-whisper'
    }
  },
};

// Для обратной совместимости
const TRANSCRIPTION_MODELS = {
  local: TRANSCRIPTION_LIBRARIES.local.models.map(m => ({ value: m.value, label: m.label })),
};

const STATUS_BADGES = {
  uploaded: 'bg-blue-200 text-blue-800',
  transcribing: 'bg-yellow-200 text-yellow-800',
  transcribed: 'bg-green-200 text-green-800',
  generating: 'bg-purple-200 text-purple-800',
  ready: 'bg-green-300 text-green-900',
  publishing: 'bg-orange-200 text-orange-800',
  published: 'bg-green-400 text-green-900',
  error: 'bg-red-200 text-red-800',
};

export default function YouTube() {
  const [videos, setVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Состояние для отслеживания загрузок (для прогресс-баров)
  const [activeUploads, setActiveUploads] = useState([]);
  // Сохраняем ссылки на файлы для отмены загрузки
  const [uploadingFiles, setUploadingFiles] = useState(new Map());
  const [transcribing, setTranscribing] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false); // Отдельное состояние для отслеживания транскрибации
  const [transcriptionProgress, setTranscriptionProgress] = useState(0);
  const [transcriptionStatus, setTranscriptionStatus] = useState('');
  // Прогресс для каждого видео отдельно (videoId -> { progress, status })
  const [videosProgress, setVideosProgress] = useState({});
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishingTelegram, setPublishingTelegram] = useState(false);
  const [channels, setChannels] = useState([]);
  const [botTokens, setBotTokens] = useState([]);
  const [telegramConfig, setTelegramConfig] = useState({
    channel_id: '',
    channel_username: '',
    bot_token_id: '',
    bot_token: '',
    custom_text: '',
    save_custom_text: false,
    use_description_custom_text: false,
    custom_prompt: '', // Кастомный промпт для генерации поста в Telegram
    use_custom_prompt: false, // Использовать кастомный промпт
  });
  const [config, setConfig] = useState({
    transcriptionProvider: 'local', // По умолчанию используем локальный Whisper
    transcriptionModel: TRANSCRIPTION_MODELS.local[0].value, // tiny - самая легкая модель для серверов с ограниченной памятью
    generationProvider: 'openrouter', // Используется на бэкенде, но не показывается в UI
    generationModel: 'google/gemini-2.5-flash', // Используется на бэкенде, но не показывается в UI
    customText: '',
    save_description_custom_text: false, // Сохранить текст для последующих описаний
    use_description_custom_text: false, // Использовать сохраненный текст при генерации
    custom_prompt: '', // Кастомный промпт для генерации описания
    use_custom_prompt: false, // Использовать кастомный промпт
    privacy: 'private',
  });
  const [autopublishConfigs, setAutopublishConfigs] = useState({}); // { videoId: { enabled, scheduled_at, ... } }
  const [showAutopublishModal, setShowAutopublishModal] = useState(false);
  const [autopublishVideoId, setAutopublishVideoId] = useState(null);
  const [autopublishForm, setAutopublishForm] = useState({
    scheduled_at: '',
    privacy: 'private',
    publish_to_telegram: false,
    telegram_channel_id: '',
    telegram_channel_username: '',
    telegram_bot_token_id: '',
    telegram_bot_token: '',
    telegram_custom_text: '',
  });
  const [copyNotification, setCopyNotification] = useState('');
  const [deviceCapabilities, setDeviceCapabilities] = useState(null);
  const [isNativeApp, setIsNativeApp] = useState(false);
  
  // Лимиты пользователя
  const [userLimits, setUserLimits] = useState(null);
  
  // Предпросмотр поста в Telegram
  const [showTelegramPreview, setShowTelegramPreview] = useState(false);
  const [telegramPreviewPost, setTelegramPreviewPost] = useState('');
  const [generatingPreview, setGeneratingPreview] = useState(false);

  // Загрузка лимитов пользователя
  const fetchLimits = async () => {
    try {
      const response = await axios.get(`${API_URL}/youtube/limits`);
      if (response.data.limits) {
        setUserLimits(response.data.limits);
      }
    } catch (error) {
      console.warn('[YouTube] Ошибка загрузки лимитов:', error);
    }
  };

  // Проверяем возможности устройства при загрузке
  useEffect(() => {
    const checkCapabilities = async () => {
      try {
        setIsNativeApp(isNative());
        const capabilities = await canRunLocalTranscription();
        const info = await getTranscriptionInfo();
        setDeviceCapabilities({ ...capabilities, ...info });
        console.log('[YouTube] Device capabilities:', capabilities, info);
        
        // Загружаем лимиты
        await fetchLimits();
      } catch (error) {
        console.warn('[YouTube] Ошибка проверки возможностей:', error);
      }
    };
    checkCapabilities();
  }, []);

  // Функция для выбора видео из галереи (для мобильного)
  const handlePickFromGallery = async () => {
    try {
      await hapticFeedback();
      const result = await pickVideoFromGallery();
      
      if (result.file) {
        // Для веб - загружаем файл напрямую
        setUploading(true);
        const formData = new FormData();
        formData.append('file', result.file);

        const response = await axios.post(`${API_URL}/youtube/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        setSelectedVideo(response.data.video);
        await fetchVideos();
        await fetchLimits(); // Обновляем лимиты сразу
        showToast('✅ Видео успешно загружено!', 'success');
        setUploading(false);
      }
    } catch (error) {
      console.error('[YouTube] Ошибка выбора из галереи:', error);
      if (error.message !== 'Файл не выбран') {
        showToast('❌ Ошибка загрузки: ' + error.message, 'error');
      }
      setUploading(false);
    }
  };

  // Функция для шаринга результата
  const handleShareResult = async () => {
    if (!selectedVideo) return;
    
    try {
      await hapticFeedback();
      const title = selectedVideo.generated_title || selectedVideo.file_name;
      const description = selectedVideo.final_description || selectedVideo.generated_description || '';
      const text = `${title}\n\n${description.substring(0, 200)}...`;
      
      await shareContent('AI Market Hub', text, selectedVideo.youtube_url || null);
    } catch (error) {
      console.error('[YouTube] Ошибка шаринга:', error);
    }
  };

  // Функция копирования в буфер обмена
  const copyToClipboard = async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyNotification(`✅ ${fieldName} скопировано!`);
      setTimeout(() => setCopyNotification(''), 2000);
    } catch (err) {
      // Fallback для старых браузеров
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopyNotification(`✅ ${fieldName} скопировано!`);
      setTimeout(() => setCopyNotification(''), 2000);
    }
  };

  useEffect(() => {
    // Инициализация Toast
    initToast();
    
    fetchVideos();
    fetchChannels();
    fetchBotTokens();
    loadTelegramSettings();
    loadDescriptionSettings();
    
    // Автоматическое обновление списка видео каждые 3 секунды для синхронизации
    const autoRefreshInterval = setInterval(async () => {
      // Обновляем список видео, но НЕ меняем выбранное видео автоматически
      await fetchVideos(false); // false = не обновлять selectedVideo автоматически
      
      // Обновляем прогресс для всех транскрибирующихся видео
      const currentVideos = videos.filter(v => v.status === 'transcribing');
      if (currentVideos.length > 0) {
        for (const video of currentVideos) {
          try {
            const statusResponse = await axios.get(`${API_URL}/youtube/videos/${video.id}`);
            const updatedVideo = statusResponse.data.video;
            
            if (updatedVideo) {
              // Обновляем прогресс для этого видео
              if (updatedVideo.status === 'transcribing') {
                // Плавно увеличиваем прогресс
                setVideosProgress(prev => {
                  // Если прогресс еще не установлен, начинаем с 20% (процесс уже идет)
                  const currentProgress = prev[video.id]?.progress || 20;
                  const increment = currentProgress < 20 ? 1.5 : currentProgress < 50 ? 1 : currentProgress < 80 ? 0.8 : 0.5;
                  const newProgress = Math.min(currentProgress + increment, 95);
                  
                  // Сохраняем прогресс в localStorage
                  try {
                    localStorage.setItem(`transcription_progress_${video.id}`, JSON.stringify({
                      progress: newProgress,
                      timestamp: Date.now()
                    }));
                  } catch (e) {
                    // Игнорируем ошибки localStorage
                  }
                  
                  return {
                    ...prev,
                    [video.id]: {
                      progress: newProgress,
                      status: `Транскрибация в процессе... ${Math.round(newProgress)}%`
                    }
                  };
                });
              } else if (updatedVideo.status === 'transcribed') {
                // Транскрибация завершена
                setVideosProgress(prev => ({
                  ...prev,
                  [video.id]: {
                    progress: 100,
                    status: 'Транскрибация завершена!'
                  }
                }));
                // Удаляем сохраненный прогресс из localStorage
                try {
                  localStorage.removeItem(`transcription_progress_${video.id}`);
                } catch (e) {
                  // Игнорируем ошибки localStorage
                }
                // Через 3 секунды скрываем прогресс-бар
                setTimeout(() => {
                  setVideosProgress(prev => {
                    const newProgress = { ...prev };
                    delete newProgress[video.id];
                    return newProgress;
                  });
                }, 3000);
              } else if (updatedVideo.status === 'error') {
                // Ошибка транскрибации - удаляем прогресс
                setVideosProgress(prev => {
                  const newProgress = { ...prev };
                  delete newProgress[video.id];
                  return newProgress;
                });
              }
            }
          } catch (error) {
            // Игнорируем ошибки при проверке статуса
          }
        }
      }
      
      // Если есть выбранное видео, обновляем только его данные (не меняем выбор)
      if (selectedVideo) {
        axios.get(`${API_URL}/youtube/videos/${selectedVideo.id}`)
          .then(response => {
            const updatedVideo = response.data.video;
            if (updatedVideo && selectedVideo.id === updatedVideo.id) {
              // Обновляем только если это то же самое видео (не переключаем на другое)
              setSelectedVideo(updatedVideo);
            }
          })
          .catch(error => {
            // Игнорируем ошибки при автообновлении
          });
      }
    }, 3000); // Обновляем каждые 3 секунды для более быстрой синхронизации
    
    return () => {
      clearInterval(autoRefreshInterval);
    };
  }, [selectedVideo?.id]); // Перезапускаем при изменении выбранного видео

  // Загружаем настройки автопубликации для всех видео
  useEffect(() => {
    const loadAutopublishConfigs = async () => {
      const configs = {};
      for (const video of videos) {
        try {
          const response = await axios.get(`${API_URL}/youtube/videos/${video.id}/autopublish`);
          configs[video.id] = response.data.autopublish || {};
        } catch (error) {
          // Игнорируем ошибки для видео без настроек
          configs[video.id] = {};
        }
      }
      setAutopublishConfigs(configs);
    };
    
    if (videos.length > 0) {
      loadAutopublishConfigs();
    }
  }, [videos]);

  const fetchChannels = async () => {
    try {
      const response = await axios.get(`${API_URL}/channels`);
      setChannels(response.data.channels || []);
    } catch (error) {
      console.error('Ошибка загрузки каналов:', error);
    }
  };

  const fetchBotTokens = async () => {
    try {
      const response = await axios.get(`${API_URL}/bot-tokens`);
      setBotTokens(response.data.botTokens || []);
    } catch (error) {
      console.error('Ошибка загрузки токенов ботов:', error);
    }
  };

  const loadTelegramSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/youtube/settings`);
      const settings = response.data.settings || {};
      if (settings.telegram_post_custom_text?.value) {
        setTelegramConfig(prev => ({
          ...prev,
          custom_text: settings.telegram_post_custom_text.value,
        }));
      }
    } catch (error) {
      // Игнорируем ошибку, если настройки еще не сохранены
      console.warn('Настройки Telegram не найдены (это нормально при первом использовании)');
    }
  };

  const loadDescriptionSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/youtube/settings`);
      const settings = response.data.settings || {};
      if (settings.youtube_description_custom_text?.value) {
        setConfig(prev => ({
          ...prev,
          customText: settings.youtube_description_custom_text.value,
          use_description_custom_text: true, // Автоматически включаем использование сохраненного текста
        }));
      }
    } catch (error) {
      // Игнорируем ошибку, если настройки еще не сохранены
      console.warn('Настройки описания YouTube не найдены (это нормально при первом использовании)');
    }
  };

  const fetchVideos = async (updateSelected = true) => {
    try {
      const response = await axios.get(`${API_URL}/youtube/videos`);
      const previousVideos = videos; // Сохраняем предыдущий список для сравнения
      const newVideos = (response.data.videos || []).map(video => {
        // Парсим timestamps, если они приходят как строка
        if (video.timestamps && typeof video.timestamps === 'string') {
          try {
            video.timestamps = JSON.parse(video.timestamps);
          } catch (e) {
            video.timestamps = [];
          }
        }
        return video;
      });
      
      // Проверяем, есть ли видео с ошибкой публикации
      if (previousVideos && previousVideos.length > 0) {
        newVideos.forEach(video => {
          const previousVideo = previousVideos.find(v => v.id === video.id);
          // Если статус изменился на error или publish_error, показываем уведомление
          if (previousVideo && previousVideo.status !== 'error' && previousVideo.status !== 'publish_error') {
            if (video.status === 'error' || video.status === 'publish_error') {
              const errorMessage = video.error_message || video.publish_error || 'Неизвестная ошибка';
              showToast(`❌ Ошибка публикации видео "${video.file_name}": ${errorMessage}`, 'error', 8000);
            }
          }
        });
      }
      
      setVideos(newVideos);
      
      // Восстанавливаем прогресс для видео, которые уже транскрибируются из localStorage
      const transcribingVideos = newVideos.filter(v => v.status === 'transcribing');
      if (transcribingVideos.length > 0) {
        setVideosProgress(prev => {
          const newProgress = { ...prev };
          transcribingVideos.forEach(video => {
            // Если прогресс еще не установлен для этого видео, пытаемся восстановить из localStorage
            if (!newProgress[video.id]) {
              try {
                const savedProgress = localStorage.getItem(`transcription_progress_${video.id}`);
                if (savedProgress) {
                  const progressData = JSON.parse(savedProgress);
                  // Восстанавливаем прогресс, если он был сохранен недавно (менее 5 минут назад)
                  const timeDiff = Date.now() - progressData.timestamp;
                  if (timeDiff < 5 * 60 * 1000) { // 5 минут
                    newProgress[video.id] = {
                      progress: progressData.progress,
                      status: `Транскрибация в процессе... ${Math.round(progressData.progress)}%`
                    };
                  } else {
                    // Если прошло больше 5 минут, начинаем с 20%
                    newProgress[video.id] = {
                      progress: 20,
                      status: `Транскрибация в процессе... 20%`
                    };
                  }
                } else {
                  // Если нет сохраненного прогресса, начинаем с 20%
                  newProgress[video.id] = {
                    progress: 20,
                    status: `Транскрибация в процессе... 20%`
                  };
                }
              } catch (e) {
                // В случае ошибки начинаем с 20%
                newProgress[video.id] = {
                  progress: 20,
                  status: `Транскрибация в процессе... 20%`
                };
              }
            }
          });
          return newProgress;
        });
      }
      
      // Обновляем selectedVideo только если явно запрошено (не при автообновлении)
      if (updateSelected && selectedVideo) {
        const updatedVideo = newVideos.find(v => v.id === selectedVideo.id);
        if (updatedVideo) {
          // Обновляем только данные, но не меняем выбранное видео
          setSelectedVideo(prev => {
            // Если пользователь выбрал другое видео, не переключаем обратно
            if (prev && prev.id === updatedVideo.id) {
              return updatedVideo;
            }
            return prev;
          });
        }
      }
    } catch (error) {
      console.error('Ошибка загрузки видео:', error);
    }
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    // ===== ПРОВЕРКА ОФЛАЙН-РЕЖИМА =====
    if (!navigator.onLine) {
      showToast('❌ Нет подключения к интернету. Проверьте соединение и попробуйте снова.', 'error');
      event.target.value = '';
      return;
    }

    // Валидация файлов
    const validation = validateFiles(files, userLimits, videos.length);
    
    if (!validation.valid) {
      validation.errors.forEach(error => {
        showToast(`❌ ${error}`, 'error');
      });
      event.target.value = '';
      return;
    }
    
    // Показываем предупреждения
    if (validation.warnings.length > 0) {
      validation.warnings.forEach(warning => {
        showToast(`⚠️ ${warning}`, 'warning');
      });
    }

    setUploading(true);
    
    // ===== ФУНКЦИЯ ДЛЯ ПОЛУЧЕНИЯ АКТУАЛЬНОГО ТОКЕНА (для автообновления) =====
    const getAuthToken = async () => {
      try {
        // Сначала пробуем получить токен из Supabase
        const supabaseToken = await authHelpers.getAccessToken();
        if (supabaseToken) {
          return supabaseToken;
        }
        // Fallback на старую систему session_token
        return localStorage.getItem('session_token');
      } catch (error) {
        console.warn('[YouTube] Ошибка получения токена:', error);
        return localStorage.getItem('session_token');
      }
    };
    
    // Получаем начальный токен
    let authToken = await getAuthToken();
    
    if (!authToken) {
      console.warn('[YouTube] Токен авторизации не найден');
      showToast('❌ Требуется авторизация. Пожалуйста, войдите в систему.', 'error');
      setUploading(false);
      event.target.value = '';
      return;
    }
    
    // Получаем текущего пользователя для TUS загрузки
    const currentUser = await authHelpers.getUser();
    const userId = currentUser?.id;
    
    if (!userId) {
      console.warn('[YouTube] userId не найден');
      showToast('❌ Не удалось определить пользователя. Пожалуйста, перезайдите в систему.', 'error');
      setUploading(false);
      event.target.value = '';
      return;
    }
    
    // Максимум 2 файла параллельно
    const MAX_PARALLEL_FILES = 2;
    const uploadStates = [];
    
    // Создаем состояние для каждого файла и сохраняем ссылки
    const filesMap = new Map();
    files.forEach((file) => {
      const fileId = generateFileId(file);
      const uploadState = {
        id: fileId,
        fileName: file.name,
        progress: 0,
        uploadedBytes: 0,
        totalBytes: file.size,
        speed: 0,
        error: null,
        onCancel: null
      };
      uploadStates.push(uploadState);
      filesMap.set(fileId, file);
    });
    
    setActiveUploads(uploadStates);
    setUploadingFiles(filesMap);
    
    // Небольшая задержка для отображения прогресс-бара перед началом загрузки
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Функция для обновления прогресса файла
    const updateUploadProgress = (fileId, progress, uploadedBytes, totalBytes, speed) => {
      setActiveUploads(prev => prev.map(upload => 
        upload.id === fileId 
          ? { ...upload, progress, uploadedBytes, totalBytes, speed }
          : upload
      ));
    };
    
    // Функция для обработки ошибки файла
    const handleUploadError = (fileId, error) => {
      setActiveUploads(prev => prev.map(upload => 
        upload.id === fileId 
          ? { ...upload, error: error.message || error, progress: 0 }
          : upload
      ));
    };
    
    // ===== ОБРАБОТЧИК ПОТЕРИ СВЯЗИ =====
    let isOffline = false;
    const offlineHandler = () => {
      isOffline = true;
      showToast('⚠️ Соединение потеряно. Загрузка приостановлена...', 'warning');
    };
    const onlineHandler = () => {
      if (isOffline) {
        isOffline = false;
        showToast('✅ Соединение восстановлено. Загрузка продолжается...', 'success');
      }
    };
    window.addEventListener('offline', offlineHandler);
    window.addEventListener('online', onlineHandler);
    
    // Загружаем файлы батчами (параллельно до MAX_PARALLEL_FILES)
    const uploadedVideos = [];
    
    // Функция для обновления статуса загрузки в UI
    const updateUploadStatus = (fileId, status) => {
      setActiveUploads(prev => prev.map(upload => 
        upload.id === fileId 
          ? { ...upload, status }
          : upload
      ));
    };
    
    try {
      for (let i = 0; i < files.length; i += MAX_PARALLEL_FILES) {
        // Проверяем офлайн перед каждым батчем
        if (!navigator.onLine) {
          showToast('❌ Нет подключения к интернету. Загрузка прервана. Прогресс сохранен.', 'error');
          break;
        }
        
        const batch = files.slice(i, i + MAX_PARALLEL_FILES);
        const batchPromises = batch.map(async (file) => {
          const fileId = generateFileId(file);
          
          try {
            let result;
            
            // УЛУЧШЕНО: Используем TUS для надёжной загрузки с автоматическим возобновлением
            if (UPLOAD_METHOD === 'tus') {
              // TUS Protocol - resumable uploads
              result = await uploadWithTus(file, API_URL, {
                userId: userId,
                authToken,
                onProgress: (percent, uploadedBytes, totalBytes) => {
                  // Рассчитываем скорость
                  const speed = 0; // TUS не предоставляет скорость напрямую
                  updateUploadProgress(fileId, percent, uploadedBytes, totalBytes, speed);
                },
                onSuccess: async (tusResult) => {
                  console.log('[YouTube] TUS загрузка завершена:', tusResult);
                  
                  // Обновляем статус загрузки
                  updateUploadStatus(fileId, 'complete');
                  updateUploadProgress(fileId, 100, file.size, file.size, 0);
                  
                  // Обновляем список видео
                  await fetchVideos();
                  
                  // Удаляем из активных загрузок
                  setTimeout(() => {
                    setActiveUploads(prev => prev.filter(upload => upload.id !== fileId));
                  }, 1000);
                  
                  showToast('✅ Видео успешно загружено!', 'success');
                },
                onError: (error) => {
                  console.error('[YouTube] TUS ошибка:', error);
                },
                onRetry: (attempt, delay) => {
                  showToast(`🔄 Повторная попытка ${attempt}... (ожидание ${Math.round(delay/1000)}с)`, 'info');
                  updateUploadStatus(fileId, 'retrying');
                },
                onStatusChange: (status) => {
                  updateUploadStatus(fileId, status);
                  if (status === 'merging') {
                    showToast('🔄 Обработка файла на сервере...', 'info');
                  }
                }
              });
              
              // TUS обрабатывает успех в onSuccess callback
              // Здесь просто ждём завершения промиса
              result = { success: true };
            } else {
              // Fallback: Chunked upload
              result = await uploadFileInChunks(file, API_URL, {
                onProgress: (progress, uploadedBytes, totalBytes, speed) => {
                  updateUploadProgress(fileId, progress, uploadedBytes, totalBytes, speed);
                },
                onChunkComplete: (chunkIndex, totalChunks) => {
                  // Логика при завершении чанка
                },
                onRetry: (chunkIndex, attempt, maxAttempts, delay) => {
                  showToast(`🔄 Чанк ${chunkIndex}: попытка ${attempt}/${maxAttempts}`, 'info');
                  updateUploadStatus(fileId, 'retrying');
                },
                onStatusChange: (status) => {
                  updateUploadStatus(fileId, status);
                },
                authToken,
                getAuthToken, // Функция для автообновления токена
                userId: userId,
                resume: true
              });
              
              uploadedVideos.push(result.video);
              
              // Обновляем прогресс до 100% перед удалением (только для chunked)
              updateUploadProgress(fileId, 100, file.size, file.size, 0);
              updateUploadStatus(fileId, 'complete');
              
              // Удаляем из активных загрузок через небольшую задержку (только для chunked)
              setTimeout(() => {
                setActiveUploads(prev => prev.filter(upload => upload.id !== fileId));
              }, 1000);
              
              showToast('✅ Видео успешно загружено!', 'success');
            }
            
            return { success: true, video: result?.video };
          } catch (error) {
            console.error(`[YouTube] Ошибка загрузки файла ${file.name}:`, error);
            handleUploadError(fileId, error);
            updateUploadStatus(fileId, 'error');
            
            // Удаляем из активных загрузок через 3 секунды
            setTimeout(() => {
              setActiveUploads(prev => prev.filter(upload => upload.id !== fileId));
            }, 3000);
            
            return { success: false, error: error.message };
          }
        });
        
        // Ждем завершения текущего батча
        const batchResults = await Promise.allSettled(batchPromises);
        
        // Обрабатываем результаты
        batchResults.forEach((result) => {
          if (result.status === 'fulfilled' && !result.value.success) {
            const error = result.value.error;
            showToast(`❌ Ошибка загрузки файла: ${error}`, 'error');
          } else if (result.status === 'rejected') {
            showToast(`❌ Ошибка загрузки: ${result.reason?.message || 'Неизвестная ошибка'}`, 'error');
          }
        });
      }
    } finally {
      // Удаляем обработчики событий
      window.removeEventListener('offline', offlineHandler);
      window.removeEventListener('online', onlineHandler);
    }
    
    // Обновляем список видео и лимиты
    await fetchVideos();
    await fetchLimits();
    
    // Выбираем последнее загруженное видео
    if (uploadedVideos.length > 0) {
      setSelectedVideo(uploadedVideos[uploadedVideos.length - 1]);
    }
    
    // Показываем сообщение об успехе
    const successCount = uploadedVideos.length;
    if (successCount > 0) {
      showToast(
        `✅ ${successCount === 1 ? 'Видео успешно загружено!' : `Загружено ${successCount} видео!`}`, 
        'success'
      );
    }
    
    // Очищаем активные загрузки через 5 секунд после завершения всех загрузок
    setTimeout(() => {
      setActiveUploads([]);
      setUploadingFiles(new Map());
    }, 5000);
    
    setUploading(false);
    event.target.value = ''; // Сбрасываем input после загрузки
  };

  const handleResetStatus = async (videoId) => {
    if (!confirm('Сбросить статус видео? Это позволит попробовать транскрибировать снова.')) {
      return;
    }
    
    try {
      await axios.post(`${API_URL}/youtube/videos/${videoId}/reset-status`);
      await fetchVideos();
      showToast('✅ Статус сброшен! Теперь можно попробовать транскрибировать снова.', 'success');
    } catch (error) {
      console.error('Ошибка сброса статуса:', error);
      showToast('❌ Ошибка сброса статуса: ' + (error.response?.data?.error || error.message), 'error');
    }
  };

  const handleDeleteVideo = async (videoId, event) => {
    // Останавливаем всплытие события, чтобы не выбирать видео при клике на кнопку удаления
    if (event) {
      event.stopPropagation();
    }

    if (!confirm('Удалить это видео? Файл будет удален с сервера. Это действие нельзя отменить.')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/youtube/videos/${videoId}`);
      
      // Если удаляемое видео было выбрано, очищаем selectedVideo
      if (selectedVideo?.id === videoId) {
        setSelectedVideo(null);
      }
      
      await fetchVideos();
      showToast('✅ Видео успешно удалено!', 'success');
    } catch (error) {
      console.error('Ошибка удаления видео:', error);
      showToast('❌ Ошибка удаления видео: ' + (error.response?.data?.error || error.message), 'error');
    }
  };

  const handleToggleAutopublish = async (videoId, event) => {
    if (event) {
      event.stopPropagation();
    }

    const currentConfig = autopublishConfigs[videoId] || {};
    
    // Если автопубликация уже включена, отключаем её
    if (currentConfig.auto_publish_enabled) {
      if (!confirm('Отключить автопубликацию для этого видео?')) {
        return;
      }

      try {
        await axios.post(`${API_URL}/youtube/videos/${videoId}/autopublish`, {
          enabled: false,
        });

        setAutopublishConfigs(prev => ({
          ...prev,
          [videoId]: {
            ...prev[videoId],
            auto_publish_enabled: false,
          }
        }));

        showToast('✅ Автопубликация отключена', 'success');
        await fetchVideos();
      } catch (error) {
        console.error('Ошибка отключения автопубликации:', error);
        showToast('❌ Ошибка: ' + (error.response?.data?.error || error.message), 'error');
      }
      return;
    }

    // Если включаем автопубликацию, открываем модальное окно
    setAutopublishVideoId(videoId);
    // Устанавливаем дату по умолчанию (завтра в 12:00)
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(12, 0, 0, 0);
    const defaultDateTime = tomorrow.toISOString().slice(0, 16);
    
    setAutopublishForm({
      scheduled_at: defaultDateTime,
      privacy: config.privacy,
      publish_to_telegram: false,
      telegram_channel_id: telegramConfig.channel_id || '',
      telegram_channel_username: telegramConfig.channel_username || '',
      telegram_bot_token_id: telegramConfig.bot_token_id || '',
      telegram_bot_token: telegramConfig.bot_token || '',
      telegram_custom_text: telegramConfig.custom_text || '',
    });
    setShowAutopublishModal(true);
  };

  const handleSaveAutopublish = async () => {
    if (!autopublishVideoId) return;

    if (!autopublishForm.scheduled_at) {
      showToast('⚠️ Укажите дату и время публикации', 'warning');
      return;
    }

    const scheduledDate = new Date(autopublishForm.scheduled_at);
    if (isNaN(scheduledDate.getTime())) {
      showToast('❌ Неверная дата или время', 'error');
      return;
    }

    if (scheduledDate < new Date()) {
      showToast('❌ Дата и время должны быть в будущем', 'error');
      return;
    }

    if (autopublishForm.publish_to_telegram) {
      if (!autopublishForm.telegram_channel_id && !autopublishForm.telegram_channel_username) {
          showToast('⚠️ Укажите Telegram канал', 'warning');
        return;
      }
      
      // Если выбран канал из списка, bot_token должен быть автоматически
      if (autopublishForm.telegram_channel_id) {
        const selectedChannel = channels.find(ch => ch.id.toString() === autopublishForm.telegram_channel_id);
        if (!selectedChannel?.bot_token && !autopublishForm.telegram_bot_token_id && !autopublishForm.telegram_bot_token) {
          showToast('⚠️ У выбранного канала не настроен бот-администратор. Выберите другой канал или введите токен бота вручную.', 'warning');
          return;
        }
        // Автоматически используем bot_token канала, если он есть
        if (selectedChannel?.bot_token) {
          autopublishForm.telegram_bot_token = selectedChannel.bot_token;
          autopublishForm.telegram_bot_token_id = '';
        }
      } else {
        // Если канал введен вручную, проверяем наличие токена бота
        if (!autopublishForm.telegram_bot_token_id && !autopublishForm.telegram_bot_token) {
          showToast('⚠️ Укажите токен бота', 'warning');
          return;
        }
      }
    }

    try {
      await axios.post(`${API_URL}/youtube/videos/${autopublishVideoId}/autopublish`, {
        enabled: true,
        scheduled_at: scheduledDate.toISOString(),
        privacy: autopublishForm.privacy,
        telegram_channel_id: autopublishForm.publish_to_telegram ? (autopublishForm.telegram_channel_id || null) : null,
        telegram_channel_username: autopublishForm.publish_to_telegram ? (autopublishForm.telegram_channel_username || null) : null,
        telegram_bot_token_id: autopublishForm.publish_to_telegram ? (autopublishForm.telegram_bot_token_id || null) : null,
        telegram_bot_token: autopublishForm.publish_to_telegram ? (autopublishForm.telegram_bot_token || null) : null,
        telegram_custom_text: autopublishForm.publish_to_telegram ? (autopublishForm.telegram_custom_text || null) : null,
      });

      // Обновляем локальное состояние
      setAutopublishConfigs(prev => ({
        ...prev,
        [autopublishVideoId]: {
          auto_publish_enabled: true,
          auto_publish_scheduled_at: scheduledDate.toISOString(),
          auto_publish_privacy: autopublishForm.privacy,
          auto_publish_telegram_channel_id: autopublishForm.publish_to_telegram ? autopublishForm.telegram_channel_id : null,
          auto_publish_telegram_channel_username: autopublishForm.publish_to_telegram ? autopublishForm.telegram_channel_username : null,
          auto_publish_telegram_bot_token_id: autopublishForm.publish_to_telegram ? autopublishForm.telegram_bot_token_id : null,
          auto_publish_telegram_bot_token: autopublishForm.publish_to_telegram ? autopublishForm.telegram_bot_token : null,
          auto_publish_telegram_custom_text: autopublishForm.publish_to_telegram ? autopublishForm.telegram_custom_text : null,
        }
      }));

      setShowAutopublishModal(false);
      setAutopublishVideoId(null);
      showToast(`✅ Автопубликация включена на ${scheduledDate.toLocaleString('ru-RU')}`, 'success');
      await fetchVideos();
    } catch (error) {
      console.error('Ошибка сохранения автопубликации:', error);
      showToast('❌ Ошибка: ' + (error.response?.data?.error || error.message), 'error');
    }
  };

  const handleTranscribe = async () => {
    if (!selectedVideo) return;

    // ===== ЗАЩИТА ОТ ДВОЙНОГО КЛИКА =====
    // Если уже идёт транскрибация — игнорируем
    if (isTranscribing || transcribing) {
      showToast('⏳ Транскрибация уже запущена. Дождитесь завершения.', 'warning');
      return;
    }

    // ===== ПРОВЕРКА СТАТУСА ВИДЕО =====
    // Нельзя транскрибировать видео, которое уже транскрибируется
    if (selectedVideo.status === 'transcribing') {
      showToast('⏳ Видео уже транскрибируется. Дождитесь завершения.', 'warning');
      return;
    }

    // Нельзя транскрибировать видео без файла (ещё загружается)
    if (selectedVideo.status === 'uploading' || !selectedVideo.file_path) {
      showToast('⏳ Видео ещё загружается. Дождитесь завершения загрузки.', 'warning');
      return;
    }

    // Проверяем лимит транскрибации (если видео ещё не транскрибировано)
    if (selectedVideo.status !== 'transcribed' && userLimits?.transcription?.remaining === 0) {
      showToast('❌ Достигнут дневной лимит транскрибации. Попробуйте завтра.', 'warning');
      return;
    }

    // Сбрасываем прогресс и обновляем статус видео сразу
    setTranscribing(true);
    setIsTranscribing(true);
    setTranscriptionProgress(0);
    setTranscriptionStatus('Проверка возможностей устройства...');
    
    // Инициализируем прогресс для этого видео
    setVideosProgress(prev => ({
      ...prev,
      [selectedVideo.id]: {
        progress: 0,
        status: 'Проверка возможностей устройства...'
      }
    }));
    
    // Обновляем selectedVideo со статусом "transcribing"
    setSelectedVideo({
      ...selectedVideo,
      status: 'transcribing',
      transcription_text: null,
    });
    
    // Также обновляем статус в списке videos для мгновенного отображения
    setVideos(prev => prev.map(v => 
      v.id === selectedVideo.id ? { ...v, status: 'transcribing' } : v
    ));

    // ===== ПРОВЕРЯЕМ ВОЗМОЖНОСТЬ ЛОКАЛЬНОЙ ТРАНСКРИБАЦИИ =====
    const capabilities = await canRunLocalTranscription();
    console.log('[YouTube] Проверка возможностей:', capabilities);
    
    // Если устройство поддерживает локальную транскрибацию И выбран провайдер local/auto
    const useLocalDevice = capabilities.canRun && 
                          (config.transcriptionProvider === 'local' || config.transcriptionProvider === 'auto') &&
                          !isNative(); // Пока только для браузера
    
    if (useLocalDevice) {
      console.log('[YouTube] 🖥️ Используем локальную транскрибацию на устройстве пользователя');
      setTranscriptionStatus('⬇️ Загрузка модели Whisper...');
      
      try {
        // Загружаем модель если нужно
        if (!isModelLoaded()) {
          await loadWebWhisperModel((progress) => {
            setTranscriptionProgress(Math.min(progress * 0.3, 30)); // 0-30% на загрузку модели
            setTranscriptionStatus(`⬇️ Загрузка модели: ${progress}%`);
            setVideosProgress(prev => ({
              ...prev,
              [selectedVideo.id]: {
                progress: Math.min(progress * 0.3, 30),
                status: `⬇️ Загрузка модели: ${progress}%`
              }
            }));
          });
        }
        
        setTranscriptionProgress(35);
        setTranscriptionStatus('📥 Загрузка аудио...');
        
        // Получаем аудио файл с сервера
        const audioResponse = await axios.get(
          `${API_URL}/youtube/videos/${selectedVideo.id}/audio`,
          { responseType: 'blob' }
        );
        
        setTranscriptionProgress(45);
        setTranscriptionStatus('🎤 Локальная транскрибация...');
        
        // Выполняем локальную транскрибацию
        const localResult = await transcribeLocal(audioResponse.data, (progress) => {
          const totalProgress = 45 + (progress * 0.45); // 45-90%
          setTranscriptionProgress(totalProgress);
          setTranscriptionStatus(`🎤 Транскрибация: ${Math.round(progress)}%`);
        });
        
        if (localResult.success) {
          setTranscriptionProgress(95);
          setTranscriptionStatus('💾 Сохранение результата...');
          
          // Сохраняем результат на сервер
          await axios.post(
            `${API_URL}/youtube/videos/${selectedVideo.id}/transcription-result`,
            {
              transcription_text: localResult.text,
              provider: 'local-browser',
              processingTime: localResult.processingTime,
            }
          );
          
          setTranscriptionProgress(100);
          setTranscriptionStatus('✅ Транскрибация завершена (локально)!');
          
          // Обновляем видео
          await fetchVideos();
          const updatedVideo = videos.find((v) => v.id === selectedVideo.id);
          if (updatedVideo) {
            setSelectedVideo({ ...updatedVideo, transcription_text: localResult.text, status: 'transcribed' });
          } else {
            setSelectedVideo(prev => ({ ...prev, transcription_text: localResult.text, status: 'transcribed' }));
          }
          
          setTimeout(() => {
            setTranscriptionProgress(0);
            setTranscriptionStatus('');
            setIsTranscribing(false);
            setVideosProgress(prev => {
              const newProgress = { ...prev };
              delete newProgress[selectedVideo.id];
              return newProgress;
            });
          }, 3000);
          
          setTranscribing(false);
          return; // Успешно завершили локально
        }
        
        // Локальная транскрибация не удалась — fallback на сервер
        console.log('[YouTube] Локальная транскрибация не удалась, переключаемся на сервер:', localResult.reason);
        setTranscriptionStatus('📡 Переключение на сервер...');
        
      } catch (localError) {
        console.error('[YouTube] Ошибка локальной транскрибации:', localError);
        setTranscriptionStatus('📡 Переключение на сервер...');
        // Продолжаем с серверной транскрибацией
      }
    }
    
    // ===== СЕРВЕРНАЯ ТРАНСКРИБАЦИЯ =====
    console.log('[YouTube] 📡 Используем серверную транскрибацию');
    setTranscriptionStatus('📡 Отправка на сервер...');
    setTranscriptionProgress(10);

    // Запускаем транскрибацию на сервере
    const transcribePromise = axios.post(
      `${API_URL}/youtube/videos/${selectedVideo.id}/transcribe`,
      {
        provider: config.transcriptionProvider,
        model: config.transcriptionModel,
      }
    );
    
    // Обновляем лимиты сразу после отправки запроса
    fetchLimits();

    // Показываем прогресс (проверяем статус видео с полной синхронизацией)
    const progressInterval = setInterval(async () => {
      try {
        // Проверяем статус видео напрямую с сервера
        const statusResponse = await axios.get(`${API_URL}/youtube/videos/${selectedVideo.id}`);
        const video = statusResponse.data.video;
        
        // ВСЕГДА обновляем selectedVideo для синхронизации
        if (video) {
          setSelectedVideo(video);
        }
        
        // Обновляем список видео для синхронизации статусов
        await fetchVideos();
        
        if (video.status === 'transcribed') {
          setTranscriptionProgress(100);
          setTranscriptionStatus('Транскрибация завершена!');
          clearInterval(progressInterval);
          
          // Финальное обновление для синхронизации
          await fetchVideos();
          setSelectedVideo(video);
        } else if (video.status === 'transcribing') {
          // Увеличиваем прогресс плавно
          setTranscriptionProgress(prev => {
            const increment = prev < 20 ? 1.5 : prev < 50 ? 1 : prev < 80 ? 0.8 : 0.5;
            const newProgress = Math.min(prev + increment, 95);
            setTranscriptionStatus(`Транскрибация в процессе... ${Math.round(newProgress)}%`);
            return newProgress;
          });
        } else if (video.status === 'error') {
          setTranscriptionStatus('Ошибка транскрибации');
          clearInterval(progressInterval);
        }
      } catch (error) {
        // Игнорируем ошибки при проверке статуса, но продолжаем проверку
        console.error('Ошибка проверки статуса:', error);
      }
    }, 2000); // Проверяем каждые 2 секунды для полной синхронизации

    try {
      const response = await transcribePromise;
      
      // Если транскрибация запущена в фоне (через очередь)
      if (response.data.jobId || response.data.message) {
        clearInterval(progressInterval);
        // Продолжаем проверять статус через fetchVideos с полной синхронизацией
        const videoIdBeingTranscribed = selectedVideo.id; // Сохраняем ID видео, которое транскрибируется
        const checkStatus = setInterval(async () => {
          try {
            // Получаем актуальный список видео (без изменения selectedVideo)
            await fetchVideos(false);
            
            // Получаем актуальные данные видео напрямую с сервера
            const statusResponse = await axios.get(`${API_URL}/youtube/videos/${videoIdBeingTranscribed}`);
            const updatedVideo = statusResponse.data.video;
            
            if (updatedVideo) {
              // Обновляем selectedVideo только если это то же самое видео (не переключаем на другое)
              setSelectedVideo(prev => {
                if (prev && prev.id === videoIdBeingTranscribed) {
                  return updatedVideo;
                }
                return prev; // Не меняем выбранное видео, если пользователь выбрал другое
              });
              
              // Обновляем прогресс на основе статуса
              if (updatedVideo.status === 'transcribed' && updatedVideo.transcription_text) {
                clearInterval(checkStatus);
                setTranscriptionProgress(100);
                setTranscriptionStatus('Транскрибация завершена!');
                
                // Обновляем прогресс для этого видео в общем состоянии
                setVideosProgress(prev => ({
                  ...prev,
                  [videoIdBeingTranscribed]: {
                    progress: 100,
                    status: 'Транскрибация завершена!'
                  }
                }));
                
                // Обновляем список видео еще раз для синхронизации
                await fetchVideos(false);
                
                // Обновляем selectedVideo только если это то же самое видео
                setSelectedVideo(prev => {
                  if (prev && prev.id === videoIdBeingTranscribed) {
                    return updatedVideo;
                  }
                  return prev;
                });
                
                // Скрываем прогресс-бар через 3 секунды
                setTimeout(() => {
                  setTranscriptionProgress(0);
                  setTranscriptionStatus('');
                  setIsTranscribing(false);
                  setVideosProgress(prev => {
                    const newProgress = { ...prev };
                    delete newProgress[videoIdBeingTranscribed];
                    return newProgress;
                  });
                }, 3000);
                setTranscribing(false);
              } else if (updatedVideo.status === 'error') {
                clearInterval(checkStatus);
                setTranscriptionStatus('Ошибка транскрибации');
                setTranscriptionProgress(0);
                setTranscribing(false);
                setIsTranscribing(false);
                showToast('❌ Ошибка транскрибации: ' + (updatedVideo.error_message || 'Неизвестная ошибка'), 'error');
              } else if (updatedVideo.status === 'transcribing') {
                // Продолжаем обновлять прогресс плавно во время транскрибации
                setTranscriptionProgress(prev => {
                  const increment = prev < 20 ? 1.5 : prev < 50 ? 1 : prev < 80 ? 0.8 : 0.5;
                  const newProgress = Math.min(prev + increment, 95);
                  setTranscriptionStatus(`Транскрибация в процессе... ${Math.round(newProgress)}%`);
                  return newProgress;
                });
                
                // Обновляем прогресс для этого видео в общем состоянии
                setVideosProgress(prev => {
                  const currentProgress = prev[videoIdBeingTranscribed]?.progress || 0;
                  const increment = currentProgress < 20 ? 1.5 : currentProgress < 50 ? 1 : currentProgress < 80 ? 0.8 : 0.5;
                  const newProgress = Math.min(currentProgress + increment, 95);
                  return {
                    ...prev,
                    [videoIdBeingTranscribed]: {
                      progress: newProgress,
                      status: `Транскрибация в процессе... ${Math.round(newProgress)}%`
                    }
                  };
                });
              }
            }
          } catch (error) {
            console.error('Ошибка проверки статуса:', error);
            // Продолжаем проверку даже при ошибке
          }
        }, 2000); // Проверяем каждые 2 секунды для полной синхронизации
        
        // Останавливаем проверку через 30 минут (максимальное время транскрибации)
        setTimeout(() => {
          clearInterval(checkStatus);
          if (transcribing) {
            setTranscribing(false);
            setIsTranscribing(false);
            setTranscriptionProgress(0);
            setTranscriptionStatus('');
          }
        }, 30 * 60 * 1000);
        
        // Продолжаем показывать прогресс из основного интервала
        return;
      }
      
      // Старый способ (если транскрибация вернула результат сразу)
      clearInterval(progressInterval);
      setTranscriptionProgress(100);
      setTranscriptionStatus('Транскрибация завершена!');
      
      await fetchVideos();
      const updatedVideo = videos.find((v) => v.id === selectedVideo.id);
      if (updatedVideo) {
        setSelectedVideo({ ...updatedVideo, transcription_text: response.data.transcription });
      }
      
      setTimeout(() => {
        setTranscriptionProgress(0);
        setTranscriptionStatus('');
        setIsTranscribing(false); // Сбрасываем флаг только после скрытия прогресс-бара
        fetchLimits(); // Обновляем лимиты
      }, 3000);
    } catch (error) {
      clearInterval(progressInterval);
      setTranscriptionProgress(0);
      setTranscriptionStatus('');
      setIsTranscribing(false); // Сбрасываем флаг при ошибке
      console.error('Ошибка транскрибации:', error);
      showToast('❌ Ошибка транскрибации: ' + (error.response?.data?.error || error.message), 'error');
      fetchLimits(); // Обновляем лимиты даже при ошибке
    } finally {
      setTranscribing(false);
    }
  };

  const handleGenerate = async () => {
    if (!selectedVideo) return;

    // Проверяем лимит генерации
    if (userLimits?.generation?.remaining === 0) {
      showToast('❌ Достигнут дневной лимит генерации. Попробуйте завтра.', 'warning');
      return;
    }

    setGenerating(true);
    try {
      // Определяем финальный текст для генерации
      let finalCustomText = config.customText || '';
      if (config.use_description_custom_text && config.customText) {
        finalCustomText = config.customText;
      }

      const response = await axios.post(
        `${API_URL}/youtube/videos/${selectedVideo.id}/generate`,
        {
          provider: config.generationProvider,
          model: config.generationModel,
          custom_text: finalCustomText,
          save_description_custom_text: config.save_description_custom_text,
          custom_prompt: config.use_custom_prompt && config.custom_prompt ? config.custom_prompt : null,
        }
      );
      
      // Обновляем лимиты сразу после отправки запроса
      fetchLimits();

      // Если генерация запущена в фоне (через очередь)
      if (response.data.jobId || response.data.message) {
        // Обновляем статус видео на "generating"
        setSelectedVideo({
          ...selectedVideo,
          status: 'generating',
        });

        // Продолжаем проверять статус до завершения генерации
        const videoIdBeingGenerated = selectedVideo.id;
        const checkStatus = setInterval(async () => {
          try {
            // Получаем актуальные данные видео напрямую с сервера
            const statusResponse = await axios.get(`${API_URL}/youtube/videos/${videoIdBeingGenerated}`);
            const updatedVideo = statusResponse.data.video;
            
            if (updatedVideo) {
              // Обновляем selectedVideo только если это то же самое видео
              setSelectedVideo(prev => {
                if (prev && prev.id === videoIdBeingGenerated) {
                  return updatedVideo;
                }
                return prev;
              });
              
              // Проверяем статус генерации - убеждаемся, что данные действительно сгенерированы
              const hasTitle = updatedVideo.generated_title && updatedVideo.generated_title.trim().length > 0;
              const hasDescription = updatedVideo.generated_description && updatedVideo.generated_description.trim().length > 0;
              
              if (updatedVideo.status === 'ready' && hasTitle && hasDescription) {
                clearInterval(checkStatus);
                setGenerating(false);
                
                // Парсим timestamps, если нужно
                if (updatedVideo.timestamps && typeof updatedVideo.timestamps === 'string') {
                  try {
                    updatedVideo.timestamps = JSON.parse(updatedVideo.timestamps);
                  } catch (e) {
                    updatedVideo.timestamps = [];
                  }
                }
                
                setSelectedVideo(updatedVideo);
                await fetchVideos();
                showToast('✅ Название и описание сгенерированы!', 'success');
              } else if (updatedVideo.status === 'error') {
                clearInterval(checkStatus);
                setGenerating(false);
                showToast('❌ Ошибка генерации: ' + (updatedVideo.error_message || 'Неизвестная ошибка'), 'error');
              } else if (updatedVideo.status === 'ready' && (!hasTitle || !hasDescription)) {
                // Статус ready, но данных еще нет - продолжаем проверку
                // Это может быть, если данные еще не успели обновиться в БД
                console.log('Статус ready, но данные еще не готовы, продолжаем проверку...');
              }
            }
          } catch (error) {
            console.error('Ошибка проверки статуса генерации:', error);
            // Продолжаем проверку даже при ошибке
          }
        }, 2000); // Проверяем каждые 2 секунды
        
        // Останавливаем проверку через 10 минут (максимальное время генерации)
        setTimeout(() => {
          clearInterval(checkStatus);
          if (generating) {
            setGenerating(false);
            showToast('⏱️ Генерация занимает больше времени, чем ожидалось. Проверьте статус позже.', 'info');
          }
        }, 10 * 60 * 1000);
        
        return;
      }

      // Если генерация вернула результат сразу (старый способ)
      // Обновляем selectedVideo с новыми данными
      setSelectedVideo({
        ...selectedVideo,
        generated_title: response.data.title,
        generated_description: response.data.description,
        final_description: response.data.description,
        timestamps: response.data.timestamps || [],
        status: 'ready',
      });
      
      // Обновляем список видео
      await fetchVideos();
      
      // Обновляем selectedVideo из обновленного списка
      const updatedVideos = await axios.get(`${API_URL}/youtube/videos`).then(res => res.data.videos || []);
      const updatedVideo = updatedVideos.find((v) => v.id === selectedVideo.id);
      if (updatedVideo) {
        // Парсим timestamps, если нужно
        if (updatedVideo.timestamps && typeof updatedVideo.timestamps === 'string') {
          try {
            updatedVideo.timestamps = JSON.parse(updatedVideo.timestamps);
          } catch (e) {
            updatedVideo.timestamps = [];
          }
        }
        setSelectedVideo(updatedVideo);
      }
      
      await fetchLimits(); // Обновляем лимиты
      showToast('✅ Название и описание сгенерированы!', 'success');
    } catch (error) {
      console.error('Ошибка генерации:', error);
      showToast('❌ Ошибка генерации: ' + (error.response?.data?.error || error.message), 'error');
    } finally {
      setGenerating(false);
    }
  };

  const handlePublish = async () => {
    if (!selectedVideo) return;

    if (!confirm('Опубликовать видео на YouTube?')) return;

    setPublishing(true);
    try {
      const response = await axios.post(
        `${API_URL}/youtube/videos/${selectedVideo.id}/publish`,
        {
          title: selectedVideo.generated_title,
          description: selectedVideo.final_description || selectedVideo.generated_description,
          privacy: config.privacy,
        }
      );

      await fetchVideos();
      
      // Проверяем, не изменился ли статус на error после публикации
      const updatedVideoList = await axios.get(`${API_URL}/youtube/videos`);
      const updatedVideo = updatedVideoList.data.videos?.find(v => v.id === selectedVideo.id);
      
      if (updatedVideo && (updatedVideo.status === 'error' || updatedVideo.status === 'publish_error')) {
        const errorMessage = updatedVideo.error_message || updatedVideo.publish_error || 'Неизвестная ошибка';
        showToast(`❌ Ошибка публикации: ${errorMessage}`, 'error', 8000);
      } else if (response.data.videoUrl) {
        showToast(`✅ Видео опубликовано! URL: ${response.data.videoUrl}`, 'success', 5000);
      }
    } catch (error) {
      console.error('Ошибка публикации:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Неизвестная ошибка';
      showToast(`❌ Ошибка публикации: ${errorMessage}`, 'error', 8000);
      
      // Обновляем список видео, чтобы увидеть актуальный статус
      await fetchVideos();
    } finally {
      setPublishing(false);
    }
  };

  // Генерация предпросмотра поста
  const handleGeneratePreview = async () => {
    if (!selectedVideo) return;

    // Проверяем наличие транскрибации
    if (!selectedVideo.transcription_text && selectedVideo.status !== 'published') {
      showToast('⚠️ Сначала нужно транскрибировать видео', 'warning');
      return;
    }

    // Проверяем лимит
    if (userLimits?.telegram_post?.remaining === 0) {
      showToast(`⚠️ Достигнут дневной лимит генерации постов (${userLimits.telegram_post.limit} раз в день). Попробуйте завтра.`, 'warning');
      return;
    }

    setGeneratingPreview(true);
    try {
      const response = await axios.post(
        `${API_URL}/youtube/videos/${selectedVideo.id}/preview-telegram`,
        {
          custom_text: telegramConfig.custom_text || '',
          use_description_custom_text: telegramConfig.use_description_custom_text,
          custom_prompt: telegramConfig.use_custom_prompt && telegramConfig.custom_prompt ? telegramConfig.custom_prompt : null,
        }
      );

      setTelegramPreviewPost(response.data.post);
      setShowTelegramPreview(true);
      
      // Обновляем лимиты
      await fetchLimits();
    } catch (error) {
      console.error('Ошибка генерации предпросмотра:', error);
      showToast('❌ Ошибка генерации: ' + (error.response?.data?.error || error.message), 'error');
    } finally {
      setGeneratingPreview(false);
    }
  };

  // Публикация готового поста в Telegram
  const handlePublishTelegram = async () => {
    if (!selectedVideo || !telegramPreviewPost) return;

    // Проверяем наличие канала
    if (!telegramConfig.channel_id && !telegramConfig.channel_username) {
      showToast('⚠️ Выберите Telegram канал или введите username канала', 'warning');
      return;
    }

    // Если выбран канал из списка, bot_token должен быть автоматически
    let botToken = telegramConfig.bot_token;
    if (telegramConfig.channel_id) {
      const selectedChannel = channels.find(ch => ch.id.toString() === telegramConfig.channel_id);
      if (!selectedChannel?.bot_token && !telegramConfig.bot_token_id && !telegramConfig.bot_token) {
        showToast('⚠️ У выбранного канала не настроен бот-администратор.', 'warning');
        return;
      }
      if (selectedChannel?.bot_token) {
        botToken = selectedChannel.bot_token;
      }
    } else {
      if (!telegramConfig.bot_token_id && !telegramConfig.bot_token) {
        showToast('⚠️ Выберите бота из списка или введите токен бота', 'warning');
        return;
      }
    }

    setPublishingTelegram(true);
    try {
      const response = await axios.post(
        `${API_URL}/youtube/videos/${selectedVideo.id}/publish-telegram`,
        {
          channel_id: telegramConfig.channel_id || null,
          channel_username: telegramConfig.channel_username || null,
          bot_token_id: telegramConfig.bot_token_id || null,
          bot_token: botToken || null,
          post_text: telegramPreviewPost, // Передаём готовый текст
        }
      );

      showToast('✅ Пост успешно опубликован в Telegram!', 'success');
      setShowTelegramPreview(false);
      setTelegramPreviewPost('');
      
      await fetchVideos();
    } catch (error) {
      console.error('Ошибка публикации в Telegram:', error);
      showToast('❌ Ошибка публикации: ' + (error.response?.data?.error || error.message), 'error');
    } finally {
      setPublishingTelegram(false);
    }
  };

  const selectVideo = async (video) => {
    try {
      // Сначала устанавливаем базовые данные для быстрого отображения
      setSelectedVideo({ ...video, status: video.status || 'uploaded' });
      
      // Затем загружаем полные данные с сервера (включая transcription_text)
      const response = await axios.get(`${API_URL}/youtube/videos/${video.id}`);
      const fullVideo = response.data.video;
      
      if (fullVideo) {
        // Парсим timestamps, если нужно
        if (fullVideo.timestamps && typeof fullVideo.timestamps === 'string') {
          try {
            fullVideo.timestamps = JSON.parse(fullVideo.timestamps);
          } catch (e) {
            fullVideo.timestamps = [];
          }
        } else if (!fullVideo.timestamps) {
          fullVideo.timestamps = [];
        }
        
        // Устанавливаем полные данные
        setSelectedVideo(fullVideo);
        
        // Загружаем кастомный текст из описания, если есть
        if (fullVideo.custom_text) {
          setTelegramConfig(prev => ({
            ...prev,
            use_description_custom_text: true,
          }));
        }
        
        console.log('[YouTube] Загружены полные данные видео:', {
          id: fullVideo.id,
          status: fullVideo.status,
          hasTranscription: !!fullVideo.transcription_text,
          hasTitle: !!fullVideo.generated_title,
          hasDescription: !!fullVideo.generated_description
        });
      }
    } catch (error) {
      console.error('Ошибка при выборе видео:', error);
      // Используем данные из списка, если не удалось загрузить полные
      const videoCopy = { ...video };
      if (videoCopy.timestamps && typeof videoCopy.timestamps === 'string') {
        try {
          videoCopy.timestamps = JSON.parse(videoCopy.timestamps);
        } catch (e) {
          videoCopy.timestamps = [];
        }
      }
      setSelectedVideo(videoCopy);
    }
  };

  return (
    <>
      <div className="space-y-6">
      <AnimatedCard
        className="p-6"
      >
        <h1 className="text-3xl font-bold text-white mb-2 tracking-tighter">📹 YouTube Автоматизация</h1>
        <p className="text-slate-400 tracking-tight">Загрузите видео, транскрибируйте, сгенерируйте описание и опубликуйте на YouTube</p>
      </AnimatedCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 overflow-x-hidden">
        {/* Левая колонка: Список видео */}
        <AnimatedCard className="p-6">
          <h2 className="text-xl font-bold text-white mb-4">📁 Загруженные видео</h2>

          {/* Загрузка файла */}
          <div className="mb-4">
            <label className="block mb-2 text-sm font-medium text-gray-300">
              Загрузить видео
            </label>
            <div className="flex gap-2 items-center">
              <input
                type="file"
                accept="video/*"
                multiple
                onChange={handleFileUpload}
                disabled={uploading || userLimits?.upload?.remaining === 0}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-cartoon-blue file:text-white hover:file:bg-cartoon-blue-dark disabled:opacity-50 disabled:cursor-not-allowed"
              />
              {/* Кнопка выбора из галереи для мобильного */}
              {isNativeApp && (
                <button
                  onClick={handlePickFromGallery}
                  disabled={uploading}
                  className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-full text-sm font-semibold whitespace-nowrap transition-colors disabled:opacity-50"
                >
                  📱 Галерея
                </button>
              )}
            </div>
            <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-2xl">
              <p className="text-sm text-blue-800">
                <strong>ℹ️ Лимит хранения:</strong> Максимум <strong>3 видео</strong> одновременно. 
                {videos.length > 0 && (
                  <span> У вас загружено: <strong>{videos.length} из 3</strong>.</span>
                )}
                {videos.length >= 3 && (
                  <span className="block mt-1 text-red-600 font-semibold">⚠️ Достигнут лимит! Удалите старые видео для загрузки новых.</span>
                )}
              </p>
              <p className="text-xs text-blue-600 mt-2">
                ⏱️ <strong>Макс. длительность:</strong> 35 минут • 🗑️ <strong>Автоудаление:</strong> через 3 дня после загрузки
              </p>
            </div>
            
            {/* Дневные лимиты */}
            {userLimits && (
              <div className="mt-2 p-3 bg-slate-800 border border-slate-600 rounded-2xl">
                <p className="text-sm text-white font-semibold mb-2">📊 Дневные лимиты (осталось сегодня):</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  <div className={`p-2 rounded-lg ${userLimits.upload?.remaining === 0 ? 'bg-red-900/50' : 'bg-slate-700'}`}>
                    <span className="text-gray-400">📤 Загрузка:</span>
                    <span className={`ml-1 font-bold ${userLimits.upload?.remaining === 0 ? 'text-red-400' : 'text-green-400'}`}>
                      {userLimits.upload?.remaining === 0 ? '❌ исчерпан' : `✅ ${userLimits.upload?.remaining}`}
                    </span>
                  </div>
                  <div className={`p-2 rounded-lg ${userLimits.transcription?.remaining === 0 ? 'bg-red-900/50' : 'bg-slate-700'}`}>
                    <span className="text-gray-400">🎤 Транскр.:</span>
                    <span className={`ml-1 font-bold ${userLimits.transcription?.remaining === 0 ? 'text-red-400' : 'text-green-400'}`}>
                      {userLimits.transcription?.remaining === 0 ? '❌ исчерпан' : `✅ ${userLimits.transcription?.remaining}`}
                    </span>
                  </div>
                  <div className={`p-2 rounded-lg ${userLimits.generation?.remaining === 0 ? 'bg-red-900/50' : 'bg-slate-700'}`}>
                    <span className="text-gray-400">✨ Генерация:</span>
                    <span className={`ml-1 font-bold ${userLimits.generation?.remaining === 0 ? 'text-red-400' : 'text-green-400'}`}>
                      {userLimits.generation?.remaining === 0 ? '❌ исчерпан' : `✅ ${userLimits.generation?.remaining}`}
                    </span>
                  </div>
                  <div className={`p-2 rounded-lg ${userLimits.telegram_post?.remaining === 0 ? 'bg-red-900/50' : 'bg-slate-700'}`}>
                    <span className="text-gray-400">📱 Telegram:</span>
                    <span className={`ml-1 font-bold ${userLimits.telegram_post?.remaining === 0 ? 'text-red-400' : 'text-green-400'}`}>
                      {userLimits.telegram_post?.remaining === 0 ? '❌ исчерпан' : `✅ ${userLimits.telegram_post?.remaining}`}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">Лимиты обновляются в 00:00 по МСК</p>
              </div>
            )}
            {uploading && (
              <div className="mt-2 p-3 bg-yellow-50 border border-yellow-300 rounded-2xl">
                <p className="text-sm text-yellow-800">
                  ⏳ <strong>Загрузка видео...</strong>
                </p>
                <p className="text-xs text-yellow-700 mt-1">
                  ⚠️ Не обновляйте и не закрывайте страницу до завершения загрузки!
                </p>
              </div>
            )}
          </div>

          {/* Список видео */}
          <div className="space-y-2">
            {videos.map((video) => (
              <AnimatedCard
                key={video.id}
                onClick={() => selectVideo(video)}
                hover={true}
                glow={selectedVideo?.id === video.id}
                className={`p-4 cursor-pointer transition-all ${
                  selectedVideo?.id === video.id
                    ? 'bg-gradient-to-r from-purple-600/30 to-pink-600/30 border-purple-500/50'
                    : ''
                }`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className={`font-semibold ${selectedVideo?.id === video.id ? 'text-white' : 'text-white'}`}>
                      {video.file_name}
                    </p>
                    <p className={`text-sm mt-1 ${
                      selectedVideo?.id === video.id 
                        ? 'text-white/90' 
                        : 'text-gray-400 group-hover:text-white/80'
                    }`}>
                      {video.generated_title || 'Без названия'}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                        STATUS_BADGES[video.status] || STATUS_BADGES.uploaded
                      }`}>
                        {video.status}
                      </span>
                      {(video.status === 'transcribing' || video.status === 'error') && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleResetStatus(video.id);
                          }}
                          className="text-xs px-2 py-1 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded shadow-sm transition-colors"
                          title="Сбросить статус"
                        >
                          🔄
                        </button>
                      )}
                    </div>
                    {/* Прогресс-бар для транскрибирующегося видео - показываем если есть прогресс */}
                    {videosProgress[video.id] && (
                      <div className="mt-3">
                        <ProgressBar
                          progress={videosProgress[video.id].progress}
                          status={videosProgress[video.id].status}
                          showStatus={true}
                          animated={true}
                          gradient="from-purple-500 via-pink-500 to-purple-500"
                        />
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 ml-2">
                    {video.youtube_url && (
                      <a
                        href={video.youtube_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`${selectedVideo?.id === video.id ? 'text-white hover:text-gray-200' : 'text-blue-500 hover:text-blue-700'}`}
                        onClick={(e) => e.stopPropagation()}
                        title="Открыть на YouTube"
                      >
                        🔗
                      </a>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        showToast('🚧 Автопубликация скоро будет доступна!', 'info');
                      }}
                      className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                        selectedVideo?.id === video.id
                          ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                          : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                      }`}
                      title="🚧 Скоро будет"
                    >
                      ⏱️
                    </button>
                    <button
                      onClick={(e) => handleDeleteVideo(video.id, e)}
                      className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                        selectedVideo?.id === video.id
                          ? 'bg-red-500 hover:bg-red-600 text-white'
                          : 'bg-red-100 hover:bg-red-200 text-red-700'
                      }`}
                      title="Удалить видео"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              </AnimatedCard>
            ))}
          </div>
        </AnimatedCard>

        {/* Правая колонка: Работа с выбранным видео */}
        {selectedVideo && selectedVideo.id && (
          <AnimatedCard className="p-6 space-y-6">
            <h2 className="text-xl font-bold text-white">🎬 Работа с видео</h2>

            {/* Транскрибация */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-300">1️⃣ Транскрибация</h3>
              </div>
              <div className="space-y-3 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Библиотека транскрибации
                  </label>
                  <select
                    value={config.transcriptionProvider}
                    onChange={(e) =>
                      setConfig({
                        ...config,
                        transcriptionProvider: e.target.value,
                        transcriptionModel:
                          TRANSCRIPTION_MODELS[e.target.value][0].value,
                      })
                    }
                    className="w-full px-4 py-2 rounded-2xl border border-white/20 focus:ring-2 focus:ring-purple-500 bg-slate-800 text-white placeholder-gray-400"
                  >
                    <option value="local">📦 Faster-Whisper (локально, бесплатно)</option>
                  </select>
                  {TRANSCRIPTION_LIBRARIES[config.transcriptionProvider] && (
                    <p className="text-xs text-gray-400 mt-1">
                      {TRANSCRIPTION_LIBRARIES[config.transcriptionProvider].description}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Модель
                  </label>
                  <select
                    value={config.transcriptionModel}
                    onChange={(e) =>
                      setConfig({ ...config, transcriptionModel: e.target.value })
                    }
                    className="w-full px-4 py-2 rounded-2xl border border-white/20 focus:ring-2 focus:ring-purple-500 bg-slate-800 text-white placeholder-gray-400"
                  >
                    {TRANSCRIPTION_LIBRARIES[config.transcriptionProvider]?.models.map((model) => (
                      <option 
                        key={model.value} 
                        value={model.value}
                        disabled={model.disabled}
                        className={model.disabled ? 'text-gray-500' : ''}
                      >
                        {model.disabled ? `${model.label} (${model.disabledReason})` : model.label}
                      </option>
                    ))}
                  </select>
                  {/* Показываем информацию о выбранной модели */}
                  {TRANSCRIPTION_LIBRARIES[config.transcriptionProvider]?.models.find(m => m.value === config.transcriptionModel) && (() => {
                    const selectedModel = TRANSCRIPTION_LIBRARIES[config.transcriptionProvider].models.find(m => m.value === config.transcriptionModel);
                    return (
                      <div className="mt-2 p-3 bg-white/5 border border-white/10 rounded-xl">
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="font-semibold">Качество:</span> {selectedModel.quality}
                          </div>
                          <div>
                            <span className="font-semibold">Скорость:</span> {selectedModel.speed}
                          </div>
                          {selectedModel.timePerMinute && (
                            <div className="col-span-2">
                              <span className="font-semibold">⏱️ Время транскрибации:</span> {selectedModel.timePerMinute}
                            </div>
                          )}
                          {selectedModel.cost && (
                            <div className="col-span-2">
                              <span className="font-semibold">Стоимость:</span> {selectedModel.cost}
                            </div>
                          )}
                          {selectedModel.limit && (
                            <div className="col-span-2">
                              <span className="font-semibold">Лимит:</span> {selectedModel.limit}
                            </div>
                          )}
                        </div>
                        <div className="mt-2 pt-2 border-t border-white/20">
                          <p className="text-xs text-blue-700">
                            💡 <strong>Рекомендация:</strong> {selectedModel.recommended}
                          </p>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
              

              <MagneticButton
                onClick={handleTranscribe}
                disabled={
                  (transcribing && selectedVideo.status === 'transcribing') ||
                  (userLimits?.transcription?.remaining === 0 && selectedVideo.status !== 'transcribed')
                }
                variant="primary"
                className="w-full mt-4"
              >
                {transcribing 
                  ? '⏳ Транскрибирую...' 
                  : userLimits?.transcription?.remaining === 0 && selectedVideo.status !== 'transcribed'
                    ? '❌ Лимит транскрибации исчерпан'
                    : '🎤 Транскрибировать'
                }
              </MagneticButton>
              
              
              <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-2xl">
                <p className="text-xs text-blue-800">
                  ℹ️ <strong>Информация:</strong> Аудио автоматически извлекается из видео перед транскрибацией. 
                  Это уменьшает размер файла и ускоряет обработку.
                </p>
              </div>
              
              {/* Информация о транскрибации - скрыта по требованию */}
              
              {/* Предупреждение о не обновлении страницы */}
              {(isTranscribing || transcribing || selectedVideo.status === 'transcribing') && (
                <div className="mt-2 p-3 bg-yellow-50 border border-yellow-300 rounded-2xl">
                  <p className="text-xs text-yellow-800">
                    ⚠️ <strong>Важно:</strong> Не обновляйте страницу во время транскрибации! 
                    Это может привести к потере прогресса. Дождитесь завершения процесса.
                  </p>
                </div>
              )}
              
              {/* Предупреждение о пустой транскрибации */}
              {selectedVideo.status === 'transcribed' && 
               (!selectedVideo.transcription_text || selectedVideo.transcription_text.trim().length === 0) && (
                <div className="mt-4 p-3 bg-orange-500/20 border border-orange-500/50 rounded-2xl">
                  <p className="text-sm text-orange-300">
                    <strong>⚠️ Транскрибация не удалась:</strong><br/>
                    {selectedVideo.error_message || 'Не удалось распознать речь в видео. Возможно, видео содержит только музыку, шумы или речь на неподдерживаемом языке.'}
                  </p>
                  <p className="text-xs text-orange-400/70 mt-2">
                    💡 Попробуйте загрузить видео с более громким и чётким звуком
                  </p>
                </div>
              )}
              
              {selectedVideo.transcription_text && typeof selectedVideo.transcription_text === 'string' && selectedVideo.transcription_text.trim().length > 0 && (
                <div className="mt-4 p-3 bg-white/5 rounded-2xl">
                  <p className="text-sm text-gray-400">
                    <strong>Транскрибация (первые 200 символов):</strong><br/>
                    {selectedVideo.transcription_text.substring(0, 200)}...
                  </p>
                  <p className="text-xs text-gray-500 mt-2">
                    ℹ️ Полный текст транскрибации будет показан в разделе "3️⃣ Результаты" ниже
                  </p>
                </div>
              )}
            </div>

            {/* Генерация */}
            {selectedVideo.transcription_text && typeof selectedVideo.transcription_text === 'string' && (
              <div className="border-t pt-4">
                <h3 className="font-semibold text-gray-300 mb-3">2️⃣ Генерация описания</h3>
                <div className="space-y-3 mb-4">
                  {/* Кастомный промпт */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      ✨ Кастомный промпт для генерации (опционально)
                    </label>
                    <textarea
                      value={config.custom_prompt}
                      onChange={(e) =>
                        setConfig({ ...config, custom_prompt: e.target.value })
                      }
                      placeholder={`Пример промпта:
Создай название и описание в стиле:
- Заголовок: цепляющий, с эмодзи
- Описание: 800-1000 символов
- 5-6 пунктов "что в видео"
- 5-7 таймкодов
- Призыв к действию
- 10 хэштегов
Стиль: дружелюбный, неформальный`}
                      rows={6}
                      className="w-full px-4 py-2 rounded-2xl border border-white/20 focus:ring-2 focus:ring-purple-500 bg-slate-800 text-white placeholder-gray-400"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      💡 Совет: указывайте желаемую длину (до 1500 символов) и количество таймкодов (до 8), чтобы избежать обрезки
                    </p>
                    <div className="mt-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={config.use_custom_prompt}
                          onChange={(e) =>
                            setConfig({ ...config, use_custom_prompt: e.target.checked })
                          }
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-300">Использовать кастомный промпт для генерации</span>
                      </label>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Пользовательский текст с ссылками (опционально)
                    </label>
                    <textarea
                      value={config.customText}
                      onChange={(e) =>
                        setConfig({ ...config, customText: e.target.value })
                      }
                      placeholder="Вставьте текст с ссылками, который нужно добавить в описание..."
                      rows={4}
                      className="w-full px-4 py-2 rounded-2xl border border-white/20 focus:ring-2 focus:ring-purple-500 bg-slate-800 text-white placeholder-gray-400"
                    />
                    <div className="mt-2 space-y-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={config.save_description_custom_text}
                          onChange={(e) =>
                            setConfig({ ...config, save_description_custom_text: e.target.checked })
                          }
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-300">Сохранить этот текст для последующих описаний</span>
                      </label>
                      {config.customText && (
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={config.use_description_custom_text}
                            onChange={(e) =>
                              setConfig({ ...config, use_description_custom_text: e.target.checked })
                            }
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-300">Использовать этот текст при генерации описания</span>
                        </label>
                      )}
                    </div>
                  </div>
                </div>
                <MagneticButton
                  onClick={handleGenerate}
                  disabled={generating || selectedVideo.status === 'generating' || userLimits?.generation?.remaining === 0}
                  variant="primary"
                  className="w-full"
                >
                  {generating 
                    ? '⏳ Генерирую...' 
                    : userLimits?.generation?.remaining === 0 
                      ? '❌ Лимит генерации исчерпан'
                      : '✨ Сгенерировать название и описание'
                  }
                </MagneticButton>
              </div>
            )}

            {/* Результаты - показываем всегда для опубликованных видео или если есть данные */}
            {(selectedVideo.status === 'published' || selectedVideo.status === 'ready' || selectedVideo.generated_title || selectedVideo.final_description || selectedVideo.generated_description || selectedVideo.transcription_text) && (
              <div className="border-t pt-4 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-300">3️⃣ Результаты</h3>
                  {copyNotification && (
                    <span className="px-3 py-1 text-sm bg-green-100 text-green-800 rounded-lg animate-pulse">
                      {copyNotification}
                    </span>
                  )}
                </div>
                
                {/* Транскрибация - показываем полный текст или предупреждение */}
                {selectedVideo.status === 'transcribed' && 
                 (!selectedVideo.transcription_text || selectedVideo.transcription_text.trim().length === 0) && (
                  <div className="p-3 bg-orange-500/20 border border-orange-500/50 rounded-2xl">
                    <p className="text-sm text-orange-300">
                      <strong>⚠️ Транскрибация не удалась</strong>
                    </p>
                    <p className="text-xs text-orange-400/70 mt-1">
                      {selectedVideo.error_message || 'Не удалось распознать речь в видео. Возможно, видео содержит только музыку, шумы или в нём отсутствует звук.'}
                    </p>
                  </div>
                )}
                
                {selectedVideo.transcription_text && typeof selectedVideo.transcription_text === 'string' && selectedVideo.transcription_text.trim().length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium text-gray-300">
                        📝 Транскрибированный текст
                      </label>
                      <button
                        onClick={() => copyToClipboard(selectedVideo.transcription_text, 'Транскрибация')}
                        className="px-3 py-1.5 text-xs bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors flex items-center gap-1 shadow-sm"
                        title="Копировать транскрибацию"
                      >
                        📋 Копировать
                      </button>
                    </div>
                    <textarea
                      value={selectedVideo.transcription_text}
                      readOnly
                      rows={8}
                      className="w-full px-4 py-2 rounded-2xl border border-white/20 bg-white/5 font-mono text-sm"
                    />
                  </div>
                )}
                
                {/* Название */}
                {(selectedVideo.generated_title || selectedVideo.status === 'published') && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium text-gray-300">
                        🎬 Название
                      </label>
                      {selectedVideo.generated_title && (
                        <button
                          onClick={() => copyToClipboard(selectedVideo.generated_title, 'Название')}
                          className="px-3 py-1.5 text-xs bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors flex items-center gap-1 shadow-sm"
                          title="Копировать название"
                        >
                          📋 Копировать
                        </button>
                      )}
                    </div>
                    <input
                      type="text"
                      value={selectedVideo.generated_title || ''}
                      readOnly
                      className="w-full px-4 py-2 rounded-2xl border border-white/20 bg-white/5"
                    />
                  </div>
                )}
                
                {/* Описание */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-300">
                      📄 Описание
                    </label>
                    {(selectedVideo.final_description || selectedVideo.generated_description) && (
                      <button
                        onClick={() => copyToClipboard(selectedVideo.final_description || selectedVideo.generated_description, 'Описание')}
                        className="px-3 py-1.5 text-xs bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors flex items-center gap-1 shadow-sm"
                        title="Копировать описание"
                      >
                        📋 Копировать
                      </button>
                    )}
                  </div>
                  <textarea
                    value={(selectedVideo.final_description || selectedVideo.generated_description || '')}
                    readOnly
                    rows={10}
                    className="w-full px-4 py-2 rounded-2xl border border-white/20 bg-white/5 font-mono text-sm"
                    placeholder="Описание будет здесь после генерации..."
                  />
                  {!selectedVideo.final_description && !selectedVideo.generated_description && (
                    <p className="mt-2 text-sm text-gray-500">
                      ⚠️ Описание еще не сгенерировано. Нажмите "✨ Сгенерировать название и описание" выше.
                    </p>
                  )}
                </div>
                
                {/* Таймкоды */}
                {selectedVideo.timestamps && Array.isArray(selectedVideo.timestamps) && selectedVideo.timestamps.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Таймкоды
                    </label>
                    <div className="p-3 bg-white/5 rounded-2xl">
                      {selectedVideo.timestamps.map((ts, idx) => (
                        <p key={idx} className="text-sm">
                          {ts.time} - {ts.title}
                        </p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Публикация на YouTube */}
            {selectedVideo.status === 'ready' && (
              <div className="border-t pt-4">
                <h3 className="font-semibold text-gray-300 mb-3">4️⃣ Публикация на YouTube</h3>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Приватность
                  </label>
                  <select
                    value={config.privacy}
                    onChange={(e) => setConfig({ ...config, privacy: e.target.value })}
                    className="w-full px-4 py-2 rounded-2xl border border-white/20 focus:ring-2 focus:ring-purple-500 bg-slate-800 text-white placeholder-gray-400"
                  >
                    <option value="private">Приватное</option>
                    <option value="unlisted">Скрытое</option>
                    <option value="public">Публичное</option>
                  </select>
                </div>
                <MagneticButton
                  onClick={handlePublish}
                  disabled={publishing || selectedVideo.status === 'publishing'}
                  variant="success"
                  className="w-full"
                >
                  {publishing ? '⏳ Публикую...' : '🚀 Опубликовать на YouTube'}
                </MagneticButton>
              </div>
            )}

            {/* Публикация в Telegram - показываем для всех видео с транскрибацией или опубликованных */}
            {selectedVideo && (selectedVideo.transcription_text || selectedVideo.status === 'transcribed' || selectedVideo.status === 'generating' || selectedVideo.status === 'ready' || selectedVideo.status === 'published') && (
              <div className="border-t pt-4 mt-4">
                <h3 className="font-semibold text-gray-300 mb-3">5️⃣ Публикация в Telegram</h3>
                {!selectedVideo.transcription_text && selectedVideo.status !== 'published' && (
                  <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-2xl">
                    <p className="text-sm text-yellow-800">
                      ⚠️ Для публикации в Telegram нужно сначала транскрибировать видео. Нажмите "🎤 Транскрибировать" выше.
                    </p>
                  </div>
                )}
                {selectedVideo.status === 'published' && !selectedVideo.transcription_text && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-2xl">
                    <p className="text-sm text-blue-800">
                      ℹ️ Видео опубликовано, но транскрибация не найдена. Для публикации в Telegram нужна транскрибация.
                    </p>
                  </div>
                )}
                
                {/* Выбор канала */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Telegram канал
                  </label>
                  <select
                    value={telegramConfig.channel_id}
                    onChange={(e) => {
                      const channelId = e.target.value;
                      if (channelId) {
                        // Находим выбранный канал и автоматически подставляем его bot_token
                        const selectedChannel = channels.find(ch => ch.id.toString() === channelId);
                        setTelegramConfig({ 
                          ...telegramConfig, 
                          channel_id: channelId, 
                          channel_username: '',
                          // Автоматически подставляем bot_token канала
                          bot_token: selectedChannel?.bot_token || '',
                          bot_token_id: '',
                        });
                      } else {
                        setTelegramConfig({ ...telegramConfig, channel_id: '', bot_token: '', bot_token_id: '' });
                      }
                    }}
                    className="w-full px-4 py-2 rounded-2xl border border-white/20 focus:ring-2 focus:ring-purple-500 mb-2"
                  >
                    <option value="">Выберите канал из списка</option>
                    {channels.map((channel) => (
                      <option key={channel.id} value={channel.id}>
                        {channel.channel_title || channel.channel_username} ({channel.channel_username})
                        {channel.bot_token ? ' - ✅ Бот настроен' : ' - ⚠️ Бот не настроен'}
                      </option>
                    ))}
                  </select>
                  {telegramConfig.channel_id && (
                    <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-xl">
                      <p className="text-sm text-green-800">
                        ✅ Канал выбран. Бот-администратор этого канала будет использован автоматически.
                      </p>
                    </div>
                  )}
                  <div className="text-sm text-gray-500 mb-1 mt-2">или</div>
                  <input
                    type="text"
                    value={telegramConfig.channel_username}
                    onChange={(e) => setTelegramConfig({ ...telegramConfig, channel_username: e.target.value, channel_id: '', bot_token: '', bot_token_id: '' })}
                    placeholder="@channel_username или channel_username"
                    className="w-full px-4 py-2 rounded-2xl border border-white/20 focus:ring-2 focus:ring-purple-500 bg-slate-800 text-white placeholder-gray-400"
                  />
                </div>

                {/* Выбор бота - показываем только если канал введен вручную (не выбран из списка) */}
                {!telegramConfig.channel_id && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Бот для публикации
                    </label>
                    <select
                      value={telegramConfig.bot_token_id}
                      onChange={(e) => setTelegramConfig({ ...telegramConfig, bot_token_id: e.target.value, bot_token: '' })}
                      className="w-full px-4 py-2 rounded-2xl border border-white/20 focus:ring-2 focus:ring-purple-500 mb-2"
                    >
                      <option value="">Выберите бота из списка</option>
                      {botTokens.map((bot) => (
                        <option key={bot.id} value={bot.id}>
                          {bot.name || `Бот #${bot.id}`}
                        </option>
                      ))}
                    </select>
                    <div className="text-sm text-gray-500 mb-1">или</div>
                    <input
                      type="text"
                      value={telegramConfig.bot_token}
                      onChange={(e) => setTelegramConfig({ ...telegramConfig, bot_token: e.target.value, bot_token_id: '' })}
                      placeholder="Введите токен бота вручную"
                      className="w-full px-4 py-2 rounded-2xl border border-white/20 focus:ring-2 focus:ring-purple-500 bg-slate-800 text-white placeholder-gray-400"
                    />
                  </div>
                )}

                {/* Кастомный промпт для Telegram */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    ✨ Кастомный промпт для генерации поста (опционально)
                  </label>
                  <textarea
                    value={telegramConfig.custom_prompt}
                    onChange={(e) => setTelegramConfig({ ...telegramConfig, custom_prompt: e.target.value })}
                    placeholder={`Пример промпта:
Создай пост для Telegram в стиле:
- Заголовок с эмодзи
- 3-4 ключевых пункта
- Призыв посмотреть видео
- 5-7 хэштегов
Длина: 500-800 символов
Стиль: энергичный, мотивирующий`}
                    rows={6}
                    className="w-full px-4 py-2 rounded-2xl border border-white/20 focus:ring-2 focus:ring-purple-500 bg-slate-800 text-white placeholder-gray-400"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    💡 Совет: указывайте желаемую длину (500-1000 символов), чтобы пост был полным и не обрезался
                  </p>
                  <div className="mt-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={telegramConfig.use_custom_prompt}
                        onChange={(e) => setTelegramConfig({ ...telegramConfig, use_custom_prompt: e.target.checked })}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-300">Использовать кастомный промпт для генерации поста</span>
                    </label>
                  </div>
                </div>

                {/* Кастомный текст */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Дополнительный текст для поста (опционально)
                  </label>
                  <textarea
                    value={telegramConfig.custom_text}
                    onChange={(e) => setTelegramConfig({ ...telegramConfig, custom_text: e.target.value })}
                    placeholder="Вставьте текст с ссылками, который нужно добавить в пост..."
                    rows={4}
                    className="w-full px-4 py-2 rounded-2xl border border-white/20 focus:ring-2 focus:ring-purple-500 bg-slate-800 text-white placeholder-gray-400"
                  />
                  <div className="mt-2 space-y-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={telegramConfig.save_custom_text}
                        onChange={(e) => setTelegramConfig({ ...telegramConfig, save_custom_text: e.target.checked })}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-300">Сохранить этот текст для последующих постов</span>
                    </label>
                    {selectedVideo.custom_text && (
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={telegramConfig.use_description_custom_text}
                          onChange={(e) => setTelegramConfig({ ...telegramConfig, use_description_custom_text: e.target.checked })}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-300">Использовать текст из описания видео</span>
                      </label>
                    )}
                  </div>
                </div>

                <MagneticButton
                  onClick={handleGeneratePreview}
                  disabled={
                    generatingPreview || 
                    (!selectedVideo.transcription_text && selectedVideo.status !== 'published' && selectedVideo.status !== 'ready') ||
                    userLimits?.telegram_post?.remaining === 0
                  }
                  variant="primary"
                  className="w-full"
                >
                  {generatingPreview 
                    ? '⏳ Генерирую пост...' 
                    : userLimits?.telegram_post?.remaining === 0
                      ? '❌ Лимит Telegram исчерпан'
                      : '👁️ Предпросмотр поста'
                  }
                </MagneticButton>
                {userLimits?.telegram_post && (
                  <p className="mt-2 text-xs text-gray-400 text-center">
                    📱 Генерация постов: {userLimits.telegram_post.remaining} из {userLimits.telegram_post.limit} осталось сегодня
                  </p>
                )}
                {!selectedVideo.transcription_text && selectedVideo.status !== 'published' && selectedVideo.status !== 'ready' && (
                  <p className="mt-2 text-sm text-gray-500 text-center">
                    Сначала нужно транскрибировать видео
                  </p>
                )}
              </div>
            )}
          </AnimatedCard>
        )}
      </div>

      {/* Модальное окно предпросмотра поста в Telegram */}
      {showTelegramPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowTelegramPreview(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="card max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-white">👁️ Предпросмотр поста</h2>
              <button
                onClick={() => setShowTelegramPreview(false)}
                className="text-gray-400 hover:text-white text-2xl"
              >
                ✕
              </button>
            </div>
            
            {/* Предпросмотр поста */}
            <div className="bg-slate-800 rounded-xl p-4 mb-4 border border-slate-600">
              <div className="flex items-center mb-3">
                <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold mr-3">
                  📱
                </div>
                <div>
                  <p className="text-white font-semibold">
                    {telegramConfig.channel_username || 'Telegram канал'}
                  </p>
                  <p className="text-gray-400 text-xs">Предпросмотр поста</p>
                </div>
              </div>
              <div className="text-white whitespace-pre-wrap text-sm leading-relaxed">
                {telegramPreviewPost}
              </div>
            </div>

            {/* Редактирование поста */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                ✏️ Редактировать пост (опционально)
              </label>
              <textarea
                value={telegramPreviewPost}
                onChange={(e) => setTelegramPreviewPost(e.target.value)}
                rows={10}
                className="w-full px-4 py-3 rounded-xl border border-slate-600 bg-slate-800 text-white text-sm"
                placeholder="Текст поста..."
              />
            </div>

            {/* Кнопки */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowTelegramPreview(false)}
                className="flex-1 px-4 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-xl font-medium transition-colors"
              >
                ❌ Отмена
              </button>
              <button
                onClick={handlePublishTelegram}
                disabled={publishingTelegram}
                className="flex-1 px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-medium transition-colors disabled:opacity-50"
              >
                {publishingTelegram ? '⏳ Публикую...' : '📤 Опубликовать'}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Модальное окно для настройки автопубликации */}
      {showAutopublishModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowAutopublishModal(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="card max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-2xl font-bold text-white mb-4">⏰ Настройка автопубликации</h2>
            
            <div className="space-y-4">
              {/* Дата и время */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Дата и время публикации *
                </label>
                <input
                  type="datetime-local"
                  value={autopublishForm.scheduled_at}
                  onChange={(e) => setAutopublishForm({ ...autopublishForm, scheduled_at: e.target.value })}
                  className="w-full px-4 py-2 rounded-2xl border border-white/20 focus:ring-2 focus:ring-purple-500 bg-slate-800 text-white placeholder-gray-400"
                  min={new Date().toISOString().slice(0, 16)}
                />
              </div>

              {/* Приватность */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Приватность на YouTube
                </label>
                <select
                  value={autopublishForm.privacy}
                  onChange={(e) => setAutopublishForm({ ...autopublishForm, privacy: e.target.value })}
                  className="w-full px-4 py-2 rounded-2xl border border-white/20 focus:ring-2 focus:ring-purple-500 bg-slate-800 text-white placeholder-gray-400"
                >
                  <option value="private">Приватное</option>
                  <option value="unlisted">Скрытое</option>
                  <option value="public">Публичное</option>
                </select>
              </div>

              {/* Публикация в Telegram */}
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={autopublishForm.publish_to_telegram}
                    onChange={(e) => setAutopublishForm({ ...autopublishForm, publish_to_telegram: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-gray-300">Также опубликовать в Telegram</span>
                </label>
              </div>

              {/* Настройки Telegram (если включено) */}
              {autopublishForm.publish_to_telegram && (
                <div className="space-y-4 pl-6 border-l-2 border-white/10">
                  {/* Telegram канал */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Telegram канал
                    </label>
                    <select
                      value={autopublishForm.telegram_channel_id}
                      onChange={(e) => {
                        const channelId = e.target.value;
                        if (channelId) {
                          // Находим выбранный канал и автоматически подставляем его bot_token
                          const selectedChannel = channels.find(ch => ch.id.toString() === channelId);
                          setAutopublishForm({ 
                            ...autopublishForm, 
                            telegram_channel_id: channelId, 
                            telegram_channel_username: '',
                            // Автоматически подставляем bot_token канала
                            telegram_bot_token: selectedChannel?.bot_token || '',
                            telegram_bot_token_id: '',
                          });
                        } else {
                          setAutopublishForm({ ...autopublishForm, telegram_channel_id: '', telegram_bot_token: '', telegram_bot_token_id: '' });
                        }
                      }}
                      className="w-full px-4 py-2 rounded-2xl border border-white/20 focus:ring-2 focus:ring-purple-500 mb-2"
                    >
                      <option value="">Выберите канал из списка</option>
                      {channels.map((channel) => (
                        <option key={channel.id} value={channel.id}>
                          {channel.channel_name || channel.channel_username} ({channel.channel_username})
                          {channel.bot_token ? ' - ✅ Бот настроен' : ' - ⚠️ Бот не настроен'}
                        </option>
                      ))}
                    </select>
                    {autopublishForm.telegram_channel_id && (
                      <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-xl">
                        <p className="text-sm text-green-800">
                          ✅ Канал выбран. Бот-администратор этого канала будет использован автоматически.
                        </p>
                      </div>
                    )}
                    <div className="text-sm text-gray-500 mb-1 mt-2">или</div>
                    <input
                      type="text"
                      value={autopublishForm.telegram_channel_username}
                      onChange={(e) => setAutopublishForm({ ...autopublishForm, telegram_channel_username: e.target.value, telegram_channel_id: '', telegram_bot_token: '', telegram_bot_token_id: '' })}
                      placeholder="@channel_username или channel_username"
                      className="w-full px-4 py-2 rounded-2xl border border-white/20 focus:ring-2 focus:ring-purple-500 bg-slate-800 text-white placeholder-gray-400"
                    />
                  </div>

                  {/* Бот - показываем только если канал введен вручную (не выбран из списка) */}
                  {!autopublishForm.telegram_channel_id && (
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">
                        Бот для публикации
                      </label>
                      <select
                        value={autopublishForm.telegram_bot_token_id}
                        onChange={(e) => setAutopublishForm({ ...autopublishForm, telegram_bot_token_id: e.target.value, telegram_bot_token: '' })}
                        className="w-full px-4 py-2 rounded-2xl border border-white/20 focus:ring-2 focus:ring-purple-500 mb-2"
                      >
                        <option value="">Выберите бота из списка</option>
                        {botTokens.map((bot) => (
                          <option key={bot.id} value={bot.id}>
                            {bot.name || `Бот #${bot.id}`}
                          </option>
                        ))}
                      </select>
                      <div className="text-sm text-gray-500 mb-1">или</div>
                      <input
                        type="text"
                        value={autopublishForm.telegram_bot_token}
                        onChange={(e) => setAutopublishForm({ ...autopublishForm, telegram_bot_token: e.target.value, telegram_bot_token_id: '' })}
                        placeholder="Введите токен бота вручную"
                        className="w-full px-4 py-2 rounded-2xl border border-white/20 focus:ring-2 focus:ring-purple-500 bg-slate-800 text-white placeholder-gray-400"
                      />
                    </div>
                  )}

                  {/* Кастомный текст */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Дополнительный текст для поста (опционально)
                    </label>
                    <textarea
                      value={autopublishForm.telegram_custom_text}
                      onChange={(e) => setAutopublishForm({ ...autopublishForm, telegram_custom_text: e.target.value })}
                      placeholder="Вставьте текст с ссылками, который нужно добавить в пост..."
                      rows={4}
                      className="w-full px-4 py-2 rounded-2xl border border-white/20 focus:ring-2 focus:ring-purple-500 bg-slate-800 text-white placeholder-gray-400"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Кнопки */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleSaveAutopublish}
                className="flex-1 px-4 py-3 bg-green-500 hover:bg-green-600 text-white rounded-2xl font-semibold transition-colors shadow-lg"
              >
                ✅ Сохранить
              </button>
              <button
                onClick={() => {
                  setShowAutopublishModal(false);
                  setAutopublishVideoId(null);
                }}
                className="flex-1 px-4 py-3 bg-slate-600 hover:bg-slate-700 text-white rounded-2xl font-semibold transition-colors shadow-lg"
              >
                ✖️ Отмена
              </button>
            </div>
          </motion.div>
        </div>
      )}
      </div>
      
      {/* Компонент прогресса загрузки */}
      <UploadProgress 
        uploads={activeUploads.map(upload => ({
          ...upload,
          onCancel: () => {
            // Отменяем загрузку
            const file = uploadingFiles.get(upload.id);
            if (file) {
              cancelUpload(file);
            }
            setActiveUploads(prev => prev.filter(u => u.id !== upload.id));
            setUploadingFiles(prev => {
              const newMap = new Map(prev);
              newMap.delete(upload.id);
              return newMap;
            });
          }
        }))}
        onCancel={() => {
          setActiveUploads([]);
          setUploadingFiles(new Map());
        }}
      />
    </>
  );
}

